import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Row, Col } from 'react-bootstrap';

type Props = {
  submissionError: string | null;
  creating: boolean;
  onSubmit: (values: VehicleFormValues) => Promise<void>;
  initialName?: string;
  initialEmail?: string;
  initialPhone?: string;
};

export interface VehicleFormValues {
  Name: string;
  emailId: string;
  registrationNumber: string;
  phoneNumber: string;
  consentToPrivacyPolicy: boolean;
}

const vehicleSchema = Yup.object().shape({
  Name: Yup.string().required('Vehicle name is required'),
  emailId: Yup.string().email('Invalid email').required('Email is required'),
  registrationNumber: Yup.string()
    .required('Registration number is required')
    .matches(/^[A-Z]{2}\d{2}[A-Z]{1,2}\d{4}$/, 'Registration must be in format TN01BA2478 or TN02A2456'),
  phoneNumber: Yup.string()
    .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits')
    .required('Phone number is required'),
  consentToPrivacyPolicy: Yup.bool().oneOf([true], 'You must agree to the Privacy Policy').required('Required'),
});

const VehicleForm: React.FC<Props> = ({ submissionError, creating, onSubmit, initialName = '', initialEmail = '', initialPhone = '' }) => {
  const formik = useFormik({
    initialValues: {
      Name: initialName,
      emailId: initialEmail,
      registrationNumber: '',
      phoneNumber: initialPhone,
      consentToPrivacyPolicy: false,
    },
    validationSchema: vehicleSchema,
    onSubmit: async (values, { setSubmitting }) => {
      await onSubmit(values);
      setSubmitting(false);
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      {submissionError && (
        <div className="alert alert-danger" role="alert">{submissionError}</div>
      )}

      <Row className="mb-7">
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">Name</label>
          <input
            type="text"
            {...formik.getFieldProps('Name')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': (formik.touched as any).Name && (formik.errors as any).Name,
            }, {
              'is-valid': (formik.touched as any).Name && !(formik.errors as any).Name,
            })}
          />
          {(formik.touched as any).Name && (formik.errors as any).Name && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).Name}</span></div></div>
          )}
        </Col>
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">Email</label>
          <input
            type="email"
            {...formik.getFieldProps('emailId')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': (formik.touched as any).emailId && (formik.errors as any).emailId,
            }, {
              'is-valid': (formik.touched as any).emailId && !(formik.errors as any).emailId,
            })}
          />
          {(formik.touched as any).emailId && (formik.errors as any).emailId && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).emailId}</span></div></div>
          )}
        </Col>
      </Row>

      <Row className="mb-7">
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">Registration Number</label>
          <input
            type="text"
            maxLength={10}
            {...formik.getFieldProps('registrationNumber')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': (formik.touched as any).registrationNumber && (formik.errors as any).registrationNumber,
            }, {
              'is-valid': (formik.touched as any).registrationNumber && !(formik.errors as any).registrationNumber,
            })}
            onInput={e => {
              const input = e.currentTarget;
              const v = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
              input.value = v;
              formik.setFieldValue('registrationNumber', v);
            }}
          />
          {(formik.touched as any).registrationNumber && (formik.errors as any).registrationNumber && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).registrationNumber}</span></div></div>
          )}
        </Col>
        <Col md={6}>
          <label className="form-label fw-bolder text-dark fs-6">Phone Number</label>
          <input
            type="text"
            maxLength={10}
            {...formik.getFieldProps('phoneNumber')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': (formik.touched as any).phoneNumber && (formik.errors as any).phoneNumber,
            }, {
              'is-valid': (formik.touched as any).phoneNumber && !(formik.errors as any).phoneNumber,
            })}
            onInput={e => {
              const input = e.currentTarget;
              input.value = input.value.replace(/\D/g, '').slice(0, 10);
              formik.setFieldValue('phoneNumber', input.value);
            }}
          />
          {(formik.touched as any).phoneNumber && (formik.errors as any).phoneNumber && (
            <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).phoneNumber}</span></div></div>
          )}
        </Col>
      </Row>

      {/* Privacy Policy Consent */}
      <Row className="mb-5">
        <Col md={12}>
          <div className="form-check form-check-custom form-check-solid">
            <input
              className="form-check-input"
              type="checkbox"
              id="vehicle_privacy_policy_agree"
              {...formik.getFieldProps('consentToPrivacyPolicy')}
            />
            <label
              className="form-check-label fw-bold text-gray-700 fs-6"
              htmlFor="vehicle_privacy_policy_agree"
            >
              I agree to the{' '}
              <a href="https://futurisk.in/privacy-policy" target="_blank" rel="noopener noreferrer" className="link-primary">
                Privacy Policy
              </a>
              .
            </label>
            {(formik.touched as any).consentToPrivacyPolicy && (formik.errors as any).consentToPrivacyPolicy && (
              <div className="fv-plugins-message-container"><div className="fv-help-block"><span role="alert">{(formik.errors as any).consentToPrivacyPolicy}</span></div></div>
            )}
          </div>
        </Col>
      </Row>

      <div className="text-center">
        <button type="submit" className="btn btn-lg btn-primary w-100 mb-5" disabled={!formik.isValid || formik.isSubmitting}>
          Submit
        </button>
      </div>

      {creating && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center retail-overlay">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status"></div>
            <div className="mt-2">Submitting...</div>
          </div>
        </div>
      )}
    </form>
  );
};

export { VehicleForm };
